import { APP } from './tokens';
import FormatBadge from '../ui/FormatBadge';

// ─── Types ──────────────────────────────────────

export interface ArchivedItem {
  id: string;
  title: string;
  lastMessageAt: Date;
  /** File name for the format badge (e.g. "report.pdf"). Falls back to title. */
  fileName?: string;
  /** Short operation label shown before timestamp (e.g. "Summarized", "Converted"). */
  operation?: string;
}

interface ArchivedPanelProps {
  items: ArchivedItem[];
  onUnarchive: (id: string) => void;
}

// ─── Helpers ────────────────────────────────────

function timeAgo(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

// ─── Archive row ────────────────────────────────

function ArchivedRow({
  item,
  onUnarchive,
}: {
  item: ArchivedItem;
  onUnarchive: () => void;
}) {
  const badgeName = item.fileName || item.title;
  const subtitle = [item.operation, `Archived ${timeAgo(item.lastMessageAt)}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 8,
        paddingBlock: 14,
        paddingInline: 16,
        gap: 12,
        backgroundColor: '#FEFCF9',
        border: '1px solid #E0DCD4',
      }}
    >
      {/* File type badge */}
      <FormatBadge fileName={badgeName} size="sm" />

      {/* Name + subtitle */}
      <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%', overflow: 'hidden' }}>
        <div
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical' as any,
            WebkitLineClamp: 1,
            overflow: 'hidden',
            fontSize: 13,
            fontWeight: 500,
            color: '#2B2420',
            lineHeight: '16px',
            fontFamily: APP.fontFamily,
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            fontWeight: 400,
            color: '#9B8E83',
            lineHeight: '14px',
            fontFamily: APP.fontFamily,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* Unarchive button */}
      <button
        type="button"
        onClick={onUnarchive}
        style={{
          borderRadius: 6,
          paddingBlock: 5,
          paddingInline: 12,
          backgroundColor: APP.bgToolSearch,
          border: '1px solid #E4DDD4',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 12,
          fontWeight: 400,
          color: '#2B2420',
          lineHeight: '16px',
          flexShrink: 0,
        }}
      >
        Unarchive
      </button>
    </div>
  );
}

// ─── Main Panel ─────────────────────────────────

export default function ArchivedPanel({ items, onUnarchive }: ArchivedPanelProps) {
  const countLabel =
    items.length === 1
      ? '1 conversation'
      : `${items.length} conversations`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        fontFamily: APP.fontFamily,
        overflow: 'hidden',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          paddingInline: 24,
          gap: 12,
          backgroundColor: APP.bgContent,
          borderBottom: `1px solid ${APP.border}`,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: APP.textPrimary,
            lineHeight: '18px',
            flexShrink: 0,
          }}
        >
          Archived
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 400,
            color: '#9B8E83',
            lineHeight: '16px',
            flexShrink: 0,
          }}
        >
          {countLabel}
        </span>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '0%',
          overflowY: 'auto',
          paddingBlock: 28,
          paddingInline: 40,
        }}
      >
        {/* Description */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 400,
            color: '#9B8E83',
            lineHeight: '16px',
            marginBottom: 20,
          }}
        >
          Archived conversations are hidden from Recent. Restore them anytime.
        </div>

        {/* Archived items */}
        {items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((item) => (
              <ArchivedRow
                key={item.id}
                item={item}
                onUnarchive={() => onUnarchive(item.id)}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              paddingBlock: 40,
              fontSize: 13,
              color: '#B8A898',
              lineHeight: '16px',
            }}
          >
            No archived conversations
          </div>
        )}
      </div>
    </div>
  );
}
